import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import { EN_GLOSSARY_GROUPS, EN_GLOSSARY_TERMS, type EnglishGlossaryTerm } from "@/app/seo-glossary-en";

const groupAccents: Record<EnglishGlossaryTerm["group"], string> = {
  core: "#A3E635",
  role: "#3B82F6",
  marketplace: "#8B5CF6",
  engineering: "#14B8A6",
  console: "#F97316",
  "billing-governance": "#FACC15",
};

export default function EnglishGlossaryPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <ContentHero
        eyebrow="GLOSSARY · ENGLISH"
        eyebrowColor="#A3E635"
        titleTop="A glossary"
        titleBottom="that does not drift"
        bottomColor="#A3E635"
        lead="This page defines the canonical CALL ANYTHING vocabulary for English readers, README references, compare pages, and answer-engine extraction. These terms should stay stable across the site."
        meta={[`${EN_GLOSSARY_TERMS.length} terms`, "DefinedTermSet schema", "English mirror"]}
        breadcrumbs={[
          { label: "English Home", href: "/en/" },
          { label: "Glossary" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <nav
          className="mb-10 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          aria-label="Glossary groups"
        >
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">Group index</p>
          <ul className="mt-4 flex flex-wrap gap-3">
            {EN_GLOSSARY_GROUPS.map((group) => (
              <li key={group.id}>
                <a
                  href={`#group-${group.id}`}
                  className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 text-xs font-black uppercase text-black transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
                  style={{ boxShadow: `4px 4px 0 0 ${groupAccents[group.id]}` }}
                >
                  {group.label}
                  <span className="text-[10px] font-black text-zinc-500">
                    {EN_GLOSSARY_TERMS.filter((term) => term.group === group.id).length}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-12">
          {EN_GLOSSARY_GROUPS.map((group) => {
            const terms = EN_GLOSSARY_TERMS.filter((term) => term.group === group.id);
            const accent = groupAccents[group.id];
            return (
              <section key={group.id} id={`group-${group.id}`} className="scroll-mt-24">
                <header className="border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                  <div className="flex items-center gap-3">
                    <span className="h-6 w-6 border-2 border-black" style={{ backgroundColor: accent }} aria-hidden />
                    <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">{group.label}</h2>
                  </div>
                  <p className="mt-3 text-sm font-bold leading-6 text-zinc-700 sm:text-base">{group.intro}</p>
                </header>

                <div className="mt-6 grid gap-5">
                  {terms.map((term) => (
                    <article
                      key={term.id}
                      id={term.id}
                      className="scroll-mt-24 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:p-6"
                    >
                      <header className="flex flex-wrap items-baseline justify-between gap-3">
                        <div>
                          <h3 className="text-xl font-black uppercase text-black sm:text-2xl">{term.term}</h3>
                          {term.alias ? <p className="mt-1 text-sm font-bold text-zinc-500">{term.alias}</p> : null}
                        </div>
                        <a
                          href={`#${term.id}`}
                          className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 hover:text-black"
                          aria-label={`Anchor link ${term.term}`}
                        >
                          #{term.id}
                        </a>
                      </header>

                      <p className="mt-4 text-sm font-bold leading-7 text-zinc-800 sm:text-base">{term.definition}</p>

                      {term.context ? (
                        <p className="mt-3 border-l-4 border-black bg-[#F8F6EF] p-3 text-sm font-bold leading-6 text-zinc-700">
                          {term.context}
                        </p>
                      ) : null}
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
